// import { ref } from 'vue'
import { storeToRefs } from "pinia"
import { useUiElements } from "./../store/uiElements"
import * as JSZip from 'jszip'
import { buildPdf, assembledName } from './../lib/pdfBuild'
import { cutTiles, tilesPage } from './../lib/tileSplit'


// What the visitor sees in their downloads folder.
const ZIP_NAME = 'OnlineImageOptimizer_com.zip'


const htmlTemplatePart1 = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="viewport" content="width=device-width,initial-scale=1.0">
    <title>Optimized images</title>
    <style>
    html {
        box-sizing: border-box;
        font-size: 16px;
      }
      
      *, *:before, *:after {
        box-sizing: inherit;
      }
      
      body, h1, h2, h3, h4, h5, h6, p {
        margin: 0;
        padding: 0;
        font-weight: normal;
      }
      
      img {
        max-width: 100%;
        height: auto;
      }
    </style>
  </head>
  <body>
  `


const htmlTemplatePart2 = `
<br/>
<div style="text-align:center">Optimized with <a href="https://www.onlineimageoptimizer.com" target="_blank">Online Image
Optimizer</a></div>
  </body>
</html>`

// The page that comes in the zip, in two kinds.
//
// A DOCUMENT has a reading order and a folder of JPEGs does not show one: the
// file manager sorts by name if you are lucky and by date if you are not, and
// nothing tells you that page 10 comes after page 9 rather than after page 1.
// So the zip carries a page that puts them back in order.
//
// A SET has no order to be put back into, and that was the argument for
// leaving it out -- recorded as such, and too narrow. Order is one job; the
// other is LOOKING, and a folder of twenty optimised photographs is no easier
// to look through than a folder of twenty pages. The file manager's own
// thumbnail grid is the competition, and it does not caption, does not print,
// and does not travel inside the archive to whoever the zip is emailed to.
//
// So both get one, and the DIFFERENCE between them is the whole of the
// variation: a document is a STACK numbered by position, a set is a GRID
// captioned with filenames. Numbering a set would be inventing an order it
// does not have, and stacking twenty photographs full-width makes a page
// nobody scrolls to the end of.
//
// Everything about it is decided by where it gets opened: from a file manager,
// off a disk, by double-clicking, with no server and no network. So it is one
// self-contained file, no external stylesheet, no script, no web fonts, and
// every image referenced by a plain relative name -- which works because the
// images sit beside it in the same archive.
//
// Light, not dark like the site. It is a document rather than an interface,
// and a page of scanned paper looks wrong on black.
const escapeHtml = (value) => String(value === undefined || value === null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

function galleryPage(title, entries, kind = "document") {
    const isSet = kind === "set"

    // Written once and used twice -- at the top, where somebody who opens this
    // file lands, and at the bottom, where they finish. Two copies of the same
    // sentence in the source is how they stop being the same sentence.
    const credit = `Made with <a href="https://onlineimageoptimizer.com"
        target="_blank" rel="noopener">Online Image Optimizer</a> &mdash; a free
        tool that turns PDFs into images and makes pictures smaller, with no
        account and no watermark.`

    /* THE CAPTION IS WHAT THE PICTURE IS, and that differs between the two.
     *
     * A page is its POSITION -- "Page 7" -- and the filename is noise, since
     * it carries the width (field-report_01_1280.jpg) and would read as a page
     * number that is wrong. A photograph is its NAME: IMG_0412.jpg is how
     * somebody finds it again on their phone, and a position would be a fact
     * about this archive rather than about the picture. */
    const pages = entries.map((entry, i) => {
        const label = isSet ? escapeHtml(entry.name) : `Page ${i + 1}`
        const alt = isSet
            ? escapeHtml(entry.name)
            : `Page ${i + 1} of ${escapeHtml(title)}`
        return `      <figure>
        <figcaption>${label}</figcaption>
        <img src="${escapeHtml(entry.name)}" alt="${alt}" loading="lazy">
      </figure>`
    }).join("\n")

    return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <title>${escapeHtml(title)} &mdash; pages</title>
    <style>
      :root { color-scheme: light; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 32px 20px 56px;
        background: #f4f4f6;
        color: #1c1c22;
        font: 16px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
              Helvetica, Arial, sans-serif;
      }
      main { max-width: 900px; margin: 0 auto; }
      h1 { margin: 0 0 4px; font-size: 24px; line-height: 1.25; word-break: break-word; }
      .count { margin: 0 0 10px; color: #62626e; font-size: 15px; }
      .credit {
        margin: 0 0 30px;
        padding-bottom: 18px;
        border-bottom: 1px solid #dededf;
        color: #62626e;
        font-size: 14px;
      }
      .credit a, footer a { color: #4f39d9; }
      figure { margin: 0 0 30px; }
      ${isSet ? `
      /* A CONTACT SHEET, not a stack. Twenty photographs full-width is a page
         nobody reaches the end of; auto-fill means the browser picks the
         column count from the window, so this works on a phone and on a
         desktop with no breakpoints to get wrong -- and with no script, which
         this file may not have. */
      .sheet {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 22px;
        align-items: start;
      }
      .sheet figure { margin: 0; }
      .sheet figcaption {
        /* A long filename must not widen its column and drag the grid with
           it: minmax()'s floor is a MINIMUM, and a word that cannot wrap
           overrides it. */
        text-transform: none;
        letter-spacing: 0;
        font-weight: 600;
        word-break: break-all;
        margin: 8px 0 0;
      }
      .sheet img { border-radius: 4px; }
      ` : ""}
      figcaption {
        margin: 0 0 8px;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #62626e;
      }
      img {
        display: block;
        width: 100%;
        height: auto;
        background: #fff;
        border: 1px solid #dededf;
        border-radius: 6px;
      }
      footer {
        max-width: 900px;
        margin: 40px auto 0;
        padding-top: 20px;
        border-top: 1px solid #dededf;
        color: #62626e;
        font-size: 14px;
      }
      @media print {
        body { background: #fff; padding: 0; }
        figure { page-break-inside: avoid; margin-bottom: 0; }
        figcaption, footer { display: none; }
        img { border: 0; border-radius: 0; }
      }
    </style>
  </head>
  <body>
    <main>
      <h1>${escapeHtml(title)}</h1>
      <p class="count">${isSet
        ? `${entries.length} image${entries.length === 1 ? "" : "s"}. Every one is in
        this folder beside this file.`
        : `${entries.length} page${entries.length === 1 ? "" : "s"}, in
        order. Every image is in this folder beside this file.`}</p>
      <p class="credit">${credit}</p>
      ${isSet ? '<div class="sheet">' : ""}
${pages}
      ${isSet ? "</div>" : ""}
    </main>
    <footer>
      <p>${credit}</p>
    </footer>
  </body>
</html>
`
}


export default function () {

    
	const uiStore = useUiElements()
	const {imageVersions, /* pictureElementText */} = storeToRefs(uiStore) // needed to build downloadable zip file

    
    // Save a single image.
    //
    // This used to fetch the bytes, build an object URL and synthesise a click.
    // ImageEngine's dl_true directive makes the CDN answer with
    // Content-Disposition: attachment instead, so the browser saves it directly:
    // no second download of bytes we already have, and it works on mobile
    // Safari, where blob downloads are unreliable.
    /* Hand the file to the visitor by whichever route their device has.
     *
     * On iOS a download goes to Files, and an iPhone user asking for the
     * darkened copy wants it in Photos -- that is where it came from and where
     * Instagram looks. The share sheet has "Save Image", which puts it exactly
     * there. Everywhere else a download is what people expect, and a share
     * sheet in place of one would be a worse answer to the same question.
     *
     * Sharing needs the file itself rather than a URL, so the object URL is
     * read back into a Blob first. It also needs transient activation: called
     * too long after the tap that started it, iOS refuses, and the catch below
     * is that case as much as it is somebody pressing Cancel.
     */
    function canShareFiles(file) {
        // iOS only, deliberately. navigator.canShare is true on Android too,
        // where swapping a download for a share sheet would be a regression.
        const ios = /iP(hone|od|ad)/.test(navigator.userAgent)
            || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
        return ios && !!navigator.canShare && navigator.canShare({files: [file]})
    }

    async function shareOrDownload(url, name) {
        try {
            const blob = await (await fetch(url)).blob()
            const file = new File([blob], name, {type: blob.type || "image/jpeg"})
            if (canShareFiles(file)) {
                await navigator.share({files: [file]})
                return "shared"
            }
        } catch (err) {
            // cancelled, refused for want of activation, or the fetch failed:
            // fall through to the download, which always works
        }
        downloadSingleImage(url, name)
        return "downloaded"
    }

    function downloadSingleImage(url, name) {

        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = url
        // a same-origin hint only; the CDN's Content-Disposition is what decides
        link.download = name
        document.body.appendChild(link)
        link.click()
        link.remove()
    }


    // build zip file blob and associate to our big button link
    // ref: https://codingshiksha.com/angular/angular-13-jszip-example-to-download-compress-multiple-files-into-a-zip-file-in-browser-using-typescript/
    /* Every version in one file.
     *
     * The snippet is passed in rather than read out of the page. This used to
     * do document.getElementById("pictureElementText").innerText, and that
     * element is rendered only when the picture element is switched on and the
     * visitor is not on a phone -- so the moment the button started offering a
     * zip to everyone, the common case was a TypeError on null and no download
     * at all. The two extra files are for people who asked for the markup;
     * anyone else gets the pictures.
     *
     * Versions with no blob are skipped: a variant that failed to render would
     * otherwise put a zero-byte entry in the archive under a plausible name.
     */
    async function downloadAsZip(snippet = null) {

        const zip = new JSZip()

        for (const img of imageVersions.value) {

            if (!img.blob || img.failed) continue
            const resp = await fetch(img.blob)
            const blob = await resp.blob() 
            zip.file(img.filename, blob, {binary: true})
        }

        if (snippet) {
            zip.file("picture_elem_tags.txt", snippet)
            // adding a page that shows the effect in a standalone page
            zip.file("picture_elem_page.html", htmlTemplatePart1 + snippet + htmlTemplatePart2)
        }

        // console.dir(zip)
        // console.log("Done collecting file, let's build link to Blob")
        
        zip.generateAsync({type: 'blob'}).then((content) => {
            console.dir(content)
            const objectUrl = URL.createObjectURL(content)
            const link = document.createElement('a')

            link.download = ZIP_NAME
            link.href = objectUrl
            link.click()
        })
    }



    // The batch equivalent: one entry per image rather than one per variant,
    // and no picture element, because batch mode does not produce one.
    //
    // Names are deduplicated here rather than trusted. Two photos called
    // IMG_0001.jpg from two different phones is not unusual, and JSZip would
    // silently keep only the last one.
    async function downloadBatchZip(snippets, opts = {}) {

        const zip = new JSZip()
        const used = new Set()
        // What went in, in the order it went in. The gallery page references
        // these names, so it has to be the names AFTER deduplication rather
        // than the names asked for.
        const entries = []

        // Two photos called IMG_0001.jpg from two different phones is not
        // unusual, and JSZip would silently keep only the last one.
        const unique = (name) => {
            if (!used.has(name)) {
                used.add(name)
                return name
            }
            const dot = name.lastIndexOf(".")
            const stem = dot > 0 ? name.slice(0, dot) : name
            const ext = dot > 0 ? name.slice(dot) : ""
            let n = 2
            while (used.has(`${stem}_${n}${ext}`)) {
                n++
            }
            const out = `${stem}_${n}${ext}`
            used.add(out)
            return out
        }

        const add = async (name, objectUrl) => {
            const resp = await fetch(objectUrl)
            const filed = unique(name)
            zip.file(filed, await resp.blob(), {binary: true})
            entries.push({name: filed})
        }

        for (const item of uiStore.batchItems) {
            if (item.status !== "done") {
                continue
            }
            if (uiStore.pictureElements) {
                // every variant, because the markup below references all of
                // them and is useless without the files beside it
                for (const v of item.versions || []) {
                    if (!v.failed && v.blob) {
                        await add(v.filename, v.blob)
                    }
                }
            } else if (item.blob) {
                await add(uiStore.batchOutputName(item), item.blob)
            }
        }

        if (!used.size) {
            return
        }

        if (snippets) {
            zip.file("HTML-picture-elements.txt", snippets)
        }

        /* The zip carries a page that shows what is in it.
         *
         * WHICH page is the caller's decision -- documentGallery() in App.vue
         * decides between a document's ordered stack and a set's grid, and
         * passes `kind`. This only knows that more than one entry earns one:
         * an index over a single image is noise in a folder that already holds
         * the image.
         *
         * A DOCUMENT is numbered by POSITION, not out of the filename. The
         * names carry the width -- field-report_01_1280.jpg -- so numberIn()
         * in lib/fileOrder.js reads the LAST run of digits and would label
         * page one as page 1280. The order here is the order the table shows,
         * which is the order orderFiles() put the pages in on the way in.
         */
        if (opts.gallery && entries.length > 1) {
            zip.file("index.html",
                galleryPage(opts.gallery.title, entries, opts.gallery.kind))
        }

        const content = await zip.generateAsync({type: 'blob'})
        const objectUrl = URL.createObjectURL(content)
        const link = document.createElement('a')
        link.download = ZIP_NAME
        link.href = objectUrl
        link.click()
        // unlike the per-variant object URLs, this one has no further use
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
    }


    /* The other direction: every optimized image gathered into one PDF, one
     * image per page.
     *
     * Built here in the browser rather than on the server, because the bytes
     * are already here -- the batch fetched them from the CDN to build the zip
     * -- and a PDF holding JPEGs is a container with no compression to do. So
     * nothing is uploaded, nothing new has to be defended, and the assembled
     * document never leaves the device. See lib/pdfBuild.js.
     *
     * JPEG only, which is why the button is hidden for the other formats
     * rather than offered and then failing: PNG would need a different filter,
     * and WebP and AVIF have none at all, so they would have to be decoded and
     * re-encoded to get in -- quality loss on the way into a file whose point
     * is to be the same pictures.
     */
    async function downloadBatchPdf() {

        const images = []
        for (const item of uiStore.batchItems) {
            if (item.status !== "done" || !item.blob) {
                continue
            }
            const resp = await fetch(item.blob)
            images.push({
                bytes: new Uint8Array(await resp.arrayBuffer()),
                name: uiStore.batchOutputName(item),
            })
        }
        if (!images.length) {
            return null
        }

        const {bytes, pages, skipped} = buildPdf(images)
        if (!bytes) {
            return {pages: 0, skipped, shareOrDownload}
        }

        const objectUrl = URL.createObjectURL(
            new Blob([bytes], {type: "application/pdf"}))
        const link = document.createElement("a")
        link.download = assembledName(images[0].name)
        link.href = objectUrl
        link.click()
        // no further use for this one, unlike the per-image object URLs
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
        return {pages, skipped}
    }


    /* ONE PICTURE OUT, MANY FILES BACK. The inverse of everything else here.
     *
     * Nothing is uploaded and nothing is fetched: the cut happens on a canvas
     * against the staged file, so this never touches /upload, the CDN or the
     * versions table. /svg-to-png and /mp4-to-gif make the same bargain, and
     * here it is not even a trade -- cutting moves pixels and there is
     * nothing for a CDN to do that the browser cannot.
     *
     * The zip carries an index.html that puts the tiles back in their grid.
     * That is a sibling of galleryPage() above and exists for a sharper
     * reason: the one thing anybody wants to check about a set of tiles is
     * whether the cut was right, and the only way to see that is to
     * reassemble it. The page uses no gap, so a bad cut shows as a seam in
     * the file the visitor already has open.
     */
    async function downloadTiles({file, img, cols, rows, quality = 0.92}) {
        const stem = (file && file.name) || "picture"
        const tiles = await cutTiles({
            img, cols, rows, stem,
            sourceType: (file && file.type) || "", quality
        })
        if (!tiles.length) {
            return null
        }

        const zip = new JSZip()
        const entries = []
        for (const t of tiles) {
            // No de-duplication pass, unlike downloadBatchZip: these names are
            // generated from a grid position rather than taken from twenty
            // phones, so two of them cannot collide by construction.
            zip.file(t.name, t.blob, {binary: true})
            entries.push({name: t.name})
        }
        zip.file("index.html", tilesPage(stem, cols, entries))

        const content = await zip.generateAsync({type: "blob"})
        const objectUrl = URL.createObjectURL(content)
        const link = document.createElement("a")
        link.download = ZIP_NAME
        link.href = objectUrl
        link.click()
        // no further use for this one, unlike the per-variant object URLs
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
        return {tiles: tiles.length}
    }

    // turn bytes in Kb and Mbs
    // https://stackoverflow.com/questions/1310378/determining-image-file-size-dimensions-via-javascript
    function formatBytes(input, decimals = 1) {
        // A size nobody knows yet is not a size. Given undefined this used to
        // return the string "NaN undefined" -- Math.log(undefined) is NaN, so
        // sizes[NaN] is undefined -- and put it on the page verbatim wherever a
        // figure was read before the thing it measures existed.
        //
        // Coerced first, and that is not defensive padding: variant sizes come
        // off a content-length header, which is a STRING. Guarding with
        // Number.isFinite alone turned every optimised size on the page into an
        // em dash, which is a worse lie than the one it replaced -- it says
        // "unknown" about a number we have.
        const bytes = typeof input === "string" ? Number(input) : input;
        if (!Number.isFinite(bytes) || bytes < 0) return '\u2014';
        if (bytes === 0) return '0 Bytes';
    
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'Kb', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
        const i = Math.floor(Math.log(bytes) / Math.log(k));
    
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }


    // Put text on the clipboard, on every browser we care about.
    //
    // The old way -- select the node, then document.execCommand("copy") --
    // works on desktop and is unreliable on iOS Safari, which refuses to
    // select the contents of a readonly field. Reported from an iPhone. The
    // async Clipboard API is the supported route; execCommand stays as the
    // fallback for anything that lacks it or refuses it outside a secure
    // context.
    async function copyText(text) {
        if (navigator.clipboard && window.isSecureContext) {
            try {
                await navigator.clipboard.writeText(text)
                return true
            } catch (err) {
                /* denied or unavailable: fall through */
            }
        }
        // A detached textarea rather than the element on the page: iOS will
        // not select a readonly one, and this avoids disturbing the selection
        // the visitor may already have.
        const scratch = document.createElement("textarea")
        scratch.value = text
        scratch.setAttribute("aria-hidden", "true")
        scratch.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none"
        document.body.appendChild(scratch)
        scratch.focus()
        scratch.select()
        scratch.setSelectionRange(0, text.length)   // iOS needs the explicit range
        let ok = false
        try {
            ok = document.execCommand("copy")
        } catch (err) {
            ok = false
        }
        scratch.remove()
        return ok
    }

    // useful to copy HTML snippet with picture tags to clipboard
    function copyPictureTagsToClipboard() {
        const el = document.getElementById("pictureElementText")
        return copyText(el ? el.innerText : "")
    }

    //return list of supported file formats.
    // we take jpeg for granted, but webp and avif require a test
    // function supportedFormats() {

    //   let supArray = ["jpeg"]

    //   // SO: https://stackoverflow.com/questions/5573096/detecting-webp-support
    //   let elem = document.createElement('canvas');

    //   if (!!(elem.getContext && elem.getContext('2d'))) {
    //     // was able or not to get WebP representation
    //     if (elem.toDataURL('image/webp').indexOf('data:image/webp') == 0) {
    //       supArray.push("webp")
    //     }
    //   }      

    //   return supArray
    // }
    
	return { /* secureFileName, */ downloadAsZip, downloadBatchZip, downloadBatchPdf, downloadTiles, downloadSingleImage, shareOrDownload, formatBytes, copyPictureTagsToClipboard, copyText }
}